/**
 * Gooflord rules engine — Ben's cards, the optimised rules (docs/RULES.md), as pure, deterministic state transitions.
 *
 * Runs identically in the browser (instant feedback, solo practice) and on the server (the referee for every
 * online battle). All randomness comes from the PRNG state stored inside GameState, so the same seed and the
 * same actions always give the same game.
 *
 * Supports 1v1 and 2v2. In 2v2 the two players on a team share one level track (first to 14). Turn order is
 * team A seat 1, team A seat 2, team B seat 1, team B seat 2.
 */
import {
  DECK_RULES, TAP_ABILITIES, HAND_MAX, KING_PLUM_MAX_USES, MODE_RULES, OPENING_MAX_LEVEL, PARTY_MAX,
  type Action, type ActResult, type Card, type Catalog, type DeckInput, type Fighter, type GameEvent, type GameMode,
  type Choice, type GameState, type GameView, type PlayerState, type PlayerView,
} from '@gooflord/shared';
import { nextRandom, shuffleWith } from './rng.js';

export const isItemEntry = (s: string) => s.startsWith('__item:');
const itemEntry = (slug: string) => '__item:' + slug;
const stripItem = (s: string) => s.slice(7);

export const PER_TURN_ABILITIES = ['blob_bear', 'tooth_catcher', 'ducky', 'party_dice', 'key_master', 'chunky', 'bubble', 'acidity', 'grumpy'] as const;
export const PER_GAME_ABILITIES = ['jimbo', 'pip', 'cluck'] as const;
export const ABILITY_NEEDS_TARGET = ['blob_bear', 'tooth_catcher', 'bubble'] as const;

// ---------------------------------------------------------------- helpers

function log(st: GameState, text: string) {
  st.log.push({ id: st.nextLog++, turn: st.turnNo, text });
  if (st.log.length > 120) st.log.splice(0, st.log.length - 120);
}
const emit = (st: GameState, e: GameEvent) => { st.events.push(e); };

export function d20(st: GameState, p: PlayerState): number {
  return 1 + Math.floor(nextRandom(st) * 20) + (p.rollBonus || 0);
}

export const pidx = (st: GameState, p: PlayerState) => st.players.indexOf(p);
export const enemies = (st: GameState, p: PlayerState) => st.players.filter((x) => x.team !== p.team);
export const allies = (st: GameState, p: PlayerState) => st.players.filter((x) => x.team === p.team);
/** The "opponent" a card text refers to: the enemy in the same seat, unless an action names another enemy. */
export function opponent(st: GameState, p: PlayerState, targetPlayer?: number): PlayerState {
  if (targetPlayer != null) {
    const t = st.players[targetPlayer];
    if (t && t.team !== p.team) return t;
  }
  const en = enemies(st, p);
  return en.find((x) => x.seat === p.seat) ?? en[0];
}
export const teamDefenceless = (st: GameState, team: number) => st.players.filter((x) => x.team === team).every((x) => x.party.length === 0);

export const baseLevel = (cat: Catalog, f: Fighter) => (cat[f.slug]?.level ?? 0) + (f.bonus || 0);
/** A fighter's power this turn: printed level + permanent bonus + this-turn buffs (keyed by fighter uid). */
export const power = (p: PlayerState, f: Fighter, cat: Catalog) => baseLevel(cat, f) + (p.buffs[f.uid] || 0);
/** What an attacker sees when hitting an enemy fighter this turn (targetDebuff is keyed by the enemy fighter's uid). Bubble's shield ignores debuffs. */
export const defence = (attacker: PlayerState, f: Fighter, cat: Catalog) => baseLevel(cat, f) - (f.shielded ? 0 : attacker.debuff + (attacker.targetDebuff[f.uid] || 0));
/** All Powerful Bob Fish (level 1000) never enters a party; draws into the party skip it and leave it in the deck. */
export const canJoinParty = (cat: Catalog, s: string) => (cat[s]?.level ?? 0) < 1000;
/** Keep at most DECK_RULES.corruptedMax Corrupted cards (the first ones), dropping the excess. */
export function capCorrupted(fighters: string[], cat: Catalog): string[] {
  let n = 0;
  return fighters.filter((s) => cat[s]?.type !== 'corrupted' || n++ < DECK_RULES.corruptedMax);
}
export const lowestIdx = (cat: Catalog, party: Fighter[]) => party.reduce((best, f, k) => (baseLevel(cat, f) < baseLevel(cat, party[best]) ? k : best), 0);
export const highestIdx = (cat: Catalog, party: Fighter[]) => party.reduce((best, f, k) => (baseLevel(cat, f) > baseLevel(cat, party[best]) ? k : best), 0);
export const levelOf = (st: GameState, p: PlayerState) => st.teams[p.team].level;
/** Everyone holds HAND_MAX items. Games saved while Little Jimbo's players could hold 10 keep that limit. */
export const handMax = (p: PlayerState) => p.handMax ?? HAND_MAX;
/** Can this party fighter's Corrupted or sun ability be used (by its owner, on their turn)? */
export const corruptedReady = (f: Fighter, cat: Catalog) => !!f.ready && TAP_ABILITIES.includes(cat[f.slug]?.ability ?? '');
export const winLevel = (st: GameState) => MODE_RULES[st.mode].winLevel;
export const turnLimit = (st: GameState) => MODE_RULES[st.mode].turnLimit;

function addLevel(st: GameState, team: number, delta: number) {
  const t = st.teams[team];
  const before = t.level;
  t.level = Math.max(0, t.level + delta);
  if (t.level !== before) emit(st, { kind: 'level', team, delta: t.level - before, level: t.level });
}

/** Draw an item (reshuffling the item discard if the deck is empty). Returns the slug drawn, even when a full hand sends it to the discard. */
function drawItem(st: GameState, p: PlayerState, fromBottom = false): string | null {
  if (!p.itemDeck.length) {
    const items = p.discard.filter(isItemEntry).map(stripItem);
    if (!items.length) return null;
    p.itemDeck = shuffleWith(items, st);
    p.discard = p.discard.filter((s) => !isItemEntry(s));
  }
  const s = fromBottom ? p.itemDeck.pop() : p.itemDeck.shift();
  if (!s) return null;
  if (p.hand.length >= handMax(p)) { p.discard.push(itemEntry(s)); return s; } // hand full: it goes straight to the discard
  p.hand.push(s);
  emit(st, { kind: 'draw_item', player: pidx(st, p), slug: s });
  return s;
}
function refillFighters(st: GameState, p: PlayerState): boolean {
  if (p.fighterDeck.length) return true;
  const fighters = p.discard.filter((s) => !isItemEntry(s));
  if (!fighters.length) return false;
  p.fighterDeck = shuffleWith(fighters, st);
  p.discard = p.discard.filter(isItemEntry);
  return true;
}
function drawFighter(st: GameState, p: PlayerState): string | null {
  return refillFighters(st, p) ? p.fighterDeck.shift() ?? null : null;
}
/** Index of the card a party draw would take (skipping All Powerful Bob Fish), or -1. */
function joinIdx(p: PlayerState, cat: Catalog, fromBottom: boolean): number {
  const d = p.fighterDeck;
  if (!fromBottom) return d.findIndex((s) => canJoinParty(cat, s));
  for (let i = d.length - 1; i >= 0; i--) if (canJoinParty(cat, d[i])) return i;
  return -1;
}
const canDrawToParty = (p: PlayerState, cat: Catalog) => p.party.length < PARTY_MAX &&
  (p.fighterDeck.length ? joinIdx(p, cat, false) >= 0 : p.discard.some((s) => !isItemEntry(s) && canJoinParty(cat, s)));
function addToParty(st: GameState, p: PlayerState, slug: string, cat: Catalog, exhausted: boolean): Fighter {
  const f: Fighter = { uid: st.nextUid++, slug, exhausted, bonus: 0 };
  p.party.push(f);
  placed(st, p, f, cat);
  return f;
}
function drawFighterToParty(st: GameState, p: PlayerState, cat: Catalog, fromBottom = false): string | null {
  if (p.party.length >= PARTY_MAX || !refillFighters(st, p)) return null;
  const i = joinIdx(p, cat, fromBottom);
  if (i < 0) return null;
  const s = p.fighterDeck.splice(i, 1)[0];
  const f = addToParty(st, p, s, cat, false);
  emit(st, { kind: 'draw_fighter', player: pidx(st, p), slug: s, uid: f.uid });
  return s;
}
function discardFromHand(st: GameState, p: PlayerState, handIdx: number) {
  const s = p.hand.splice(handIdx, 1)[0];
  if (s == null) return;
  p.discard.push(itemEntry(s));
  emit(st, { kind: 'discard', player: pidx(st, p), slug: s, item: true });
}
function discardFighter(st: GameState, p: PlayerState, partyIdx: number, cat: Catalog): Fighter | null {
  const f = p.party.splice(partyIdx, 1)[0];
  if (!f) return null;
  const summoned = cat[f.slug]?.type === 'item'; // Sketch Dino / Sketch Evil Cow go back to the item pile
  p.discard.push(summoned ? itemEntry(f.slug) : f.slug);
  emit(st, { kind: 'discard', player: pidx(st, p), slug: f.slug, item: summoned });
  return f;
}

/**
 * A card entering a party. Corrupted and sun cards get their ability ready: the owner taps it to use it, once, while it
 * stays in the party. King Fish's Blob Fish join 2 levels up.
 */
function placed(st: GameState, p: PlayerState, f: Fighter, cat: Catalog) {
  const c = cat[f.slug];
  if (!c) return;
  if (cat[p.gooflord]?.ability === 'king_fish' && isBlobFish(c)) {
    f.bonus += 2;
    log(st, `King Fish: ${c.name} joins ${p.name}'s party 2 levels up.`);
  }
  if (TAP_ABILITIES.includes(c.ability ?? '')) {
    f.ready = true;
    log(st, `${c.name} joins ${p.name}'s party. Its ability is ready to use.`);
  } else if (c.ability === 'corr_daisy') log(st, `${c.name} joins ${p.name}'s party. Blob Fish beware.`);
  else if (c.ability === 'corr_beoteo') log(st, `${c.name} joins ${p.name}'s party. Every death makes it stronger.`);
}

/** Every player rolls a d20 (the user keeps their roll bonus); ties roll again. For Ben's "whoever wins / says it first" cards. */
function rollOff(st: GameState, p: PlayerState, reason: string): PlayerState {
  let pool = st.players.slice();
  for (let round = 0; round < 5 && pool.length > 1; round++) {
    const rolls = pool.map((x) => ({ x, r: 1 + Math.floor(nextRandom(st) * 20) + (x === p ? p.rollBonus || 0 : 0) }));
    for (const { x, r } of rolls) emit(st, { kind: 'roll', player: pidx(st, x), value: r, reason });
    const top = Math.max(...rolls.map((y) => y.r));
    pool = rolls.filter((y) => y.r === top).map((y) => y.x);
  }
  return pool.includes(p) ? p : pool[0];
}

/** Open a private choice: the turn waits until the chooser answers with a `choose` action. */
function ask(st: GameState, choice: Choice) {
  st.choice = choice;
  st.phase = 'choose';
}

/** Answer the open choice. With no valid picks (Auto, or ending the turn) it keeps the order / takes the first card. */
function resolveChoice(st: GameState, picks: number[] | null, cat: Catalog): ActResult {
  const ch = st.choice;
  if (!ch) return { ok: false, error: 'Nothing to choose' };
  const n = ch.options.length;
  if (picks && ch.kind === 'order' && (picks.length !== n || new Set(picks).size !== n || picks.some((i) => i >= n))) return { ok: false, error: `Put all ${n} cards in order` };
  if (picks && ch.kind === 'steal' && (picks.length !== 1 || picks[0] >= n)) return { ok: false, error: 'Pick one card' };
  const p = st.players[ch.player];
  if (ch.kind === 'order') {
    const order = picks ?? ch.options.map((_, i) => i);
    const deck = ch.pile === 'item' ? p.itemDeck : p.fighterDeck;
    const top = order.map((i) => ch.options[i]);
    deck.splice(0, n, ...top);
    log(st, `${ch.source}: ${p.name} puts the top ${n} card${n === 1 ? '' : 's'} back in a new order.`);
  } else if (ch.kind === 'steal') {
    const from = st.players[ch.from];
    const i = picks ? picks[0] : 0;
    const s = from.hand[i];
    if (s != null) {
      from.hand.splice(i, 1);
      emit(st, { kind: 'discard', player: ch.from, slug: s, item: true });
      if (p.hand.length < handMax(p)) { p.hand.push(s); emit(st, { kind: 'draw_item', player: ch.player, slug: s }); }
      else p.discard.push(itemEntry(s));
      log(st, `${ch.source}: ${p.name} steals ${cat[s]?.name ?? 'an item'} from ${from.name}.`);
    }
  }
  st.choice = null;
  st.phase = 'act';
  return { ok: true };
}

/** Use a ready Corrupted or sun fighter's ability (Ben's "when placed down" text, used when the owner chooses). */
function useCorrupted(st: GameState, p: PlayerState, f: Fighter, action: Extract<Action, { type: 'corrupted' }>, cat: Catalog): ActResult {
  const c = cat[f.slug];
  const me = pidx(st, p);
  const o = opponent(st, p, action.targetPlayer);
  const bad = (error: string): ActResult => ({ ok: false, error });
  // check the picks before anything changes
  let e: Fighter | undefined;
  if (c.ability === 'sun_cultist' || c.ability === 'sun_shadow') {
    if (action.targetPlayer != null && st.players[action.targetPlayer]?.team === p.team) return bad("That's your own team");
    e = action.target != null ? o.party[action.target] : undefined;
    if (!e) return bad('Choose an enemy fighter');
  }
  if (c.ability === 'sun_shadow') {
    const j = action.picks?.[0];
    if (j == null || j === action.target || !o.party[j]) return bad(`Choose another fighter in ${o.name}'s party for ${cat[e!.slug].name} to attack`);
  }
  if (c.ability === 'sun_toad') {
    if (p.hand.length < 5) return bad(`${c.name} needs 5 items in your hand`);
    const ps = [...new Set(action.picks ?? [])];
    if (ps.length !== 2 || ps.some((i) => p.hand[i] == null)) return bad('Choose 2 items to discard');
    if (p.party.length >= PARTY_MAX) return bad('Your party is full');
  }
  if (c.ability === 'corr_apbf' && !o.party.length) return bad(`${o.name} has no fighters`);
  f.ready = false;
  emit(st, { kind: 'ability', player: me, ability: c.ability ?? 'corrupted' });
  switch (c.ability) {
    case 'corr_chicken': {
      const d = drawFighterToParty(st, p, cat);
      if (d && cat[d].level >= 10) {
        for (let i = 0; i < 3; i++) drawItem(st, p);
        log(st, `${c.name}: ${cat[d].name} joins and ${p.name} draws 3 items.`);
      } else {
        if (p.hand.length) discardFromHand(st, p, p.hand.length - 1);
        log(st, `${c.name}: ${d ? cat[d].name + ' joins' : 'no fighter to draw'} and ${p.name} discards an item.`);
      }
      break;
    }
    case 'corr_blobber': {
      if (p.party.some((x) => x.slug === 'toxic-blobber')) {
        for (const x of p.party) { x.bonus += 10; emit(st, { kind: 'buff', player: me, uid: x.uid, delta: 10 }); }
        log(st, `${c.name} with Toxic Blobber in the party: every fighter gets +10!`);
      } else log(st, `${c.name}: no Toxic Blobber in the party, nothing happens.`);
      break;
    }
    case 'corr_bobfish': {
      const roll = d20(st, p);
      emit(st, { kind: 'roll', player: me, value: roll, need: 3, ok: roll >= 3, reason: c.name });
      if (roll >= 3) {
        let gone = 0;
        for (const e of enemies(st, p)) {
          for (let i = e.party.length - 1; i >= 0; i--) if (cat[e.party[i].slug]?.type === 'corrupted') { discardFighter(st, e, i, cat); gone++; }
          const keep: string[] = [];
          for (const s of e.fighterDeck) { if (cat[s]?.type === 'corrupted') { e.discard.push(s); gone++; } else keep.push(s); }
          e.fighterDeck = keep;
        }
        log(st, `${c.name} rolls ${roll}: ${gone} Corrupted card${gone === 1 ? '' : 's'} removed from ${o.name}'s side.`);
      } else log(st, `${c.name} rolls ${roll}. Nothing happens.`);
      break;
    }
    case 'corr_king_eye': {
      for (const x of st.players) if (x !== p) x.revealUntil = st.turnNo + 2;
      log(st, `${c.name}: everyone but ${p.name} plays with their hand showing for the next 2 rounds.`);
      break;
    }
    case 'corr_booger': {
      // 1v1: the opponent skips a turn. 2v2: one enemy skips a round, the other skips two.
      const other = enemies(st, p).find((x) => x !== o);
      o.skipTurns = (o.skipTurns ?? 0) + 1;
      if (other) other.skipTurns = (other.skipTurns ?? 0) + 2;
      log(st, `${c.name}: ${o.name} skips a turn${other ? ` and ${other.name} skips two` : ''}!`);
      break;
    }
    case 'corr_apbf': {
      const roll = d20(st, p);
      emit(st, { kind: 'roll', player: me, value: roll, need: 10, ok: roll >= 10, reason: c.name });
      if (roll >= 10) {
        let gain = 0;
        for (const v of o.party.slice()) { const i = o.party.indexOf(v); if (i >= 0) gain += kill(st, p, o, i, cat, f); }
        log(st, `${c.name} rolls ${roll}: ${o.name}'s whole party is wiped out! ${p.name} +${gain} levels.`);
      } else log(st, `${c.name} rolls ${roll}. Nothing happens.`);
      break;
    }
    case 'sun_cultist': {
      e!.burning = true;
      log(st, `${c.name} sets ${cat[e!.slug].name} on fire! It loses a level every turn.`);
      break;
    }
    case 'sun_shadow': {
      const x = e!, y = o.party[action.picks![0]];
      const xl = baseLevel(cat, x), yl = baseLevel(cat, y);
      if (xl > yl) { const g = kill(st, p, o, o.party.indexOf(y), cat, x); log(st, `${c.name} controls ${cat[x.slug].name} (${xl}): it kills its ally ${cat[y.slug].name} (${yl}). ${p.name} +${g} level.`); }
      else if (xl < yl) { const g = kill(st, p, o, o.party.indexOf(x), cat, y); log(st, `${c.name} controls ${cat[x.slug].name} (${xl}), but its ally ${cat[y.slug].name} (${yl}) kills it. ${p.name} +${g} level.`); }
      else log(st, `${c.name} controls ${cat[x.slug].name}, but it ties with ${cat[y.slug].name} at ${xl}.`);
      break;
    }
    case 'sun_toad': {
      const ps = [...new Set(action.picks!)].sort((a, b) => b - a); // highest index first so the others don't shift
      for (const i of ps) discardFromHand(st, p, i);
      const got = [drawFighterToParty(st, p, cat), drawFighterToParty(st, p, cat)].filter((s): s is string => !!s);
      log(st, `${c.name}: ${p.name} discards 2 items${got.length ? ` and ${got.map((s) => cat[s].name).join(' and ')} join${got.length === 1 ? 's' : ''} the party` : ' but has no fighter to draw'}.`);
      break;
    }
    case 'sun_siren': {
      const w = rollOff(st, p, c.name);
      addLevel(st, w.team, 2);
      log(st, `${c.name}: ${w.name} screams the loudest (highest roll) and gets 2 Gooflord levels!`);
      break;
    }
    case 'sun_gloopy': {
      const w = rollOff(st, p, c.name);
      let best = -1;
      w.discard.forEach((s, i) => { if (!isItemEntry(s) && cat[s]?.type === 'fighter' && canJoinParty(cat, s) && (best < 0 || cat[s].level > cat[w.discard[best]].level)) best = i; });
      if (best >= 0 && w.party.length < PARTY_MAX) {
        const s = w.discard.splice(best, 1)[0];
        const fi = addToParty(st, w, s, cat, false);
        emit(st, { kind: 'draw_fighter', player: pidx(st, w), slug: s, uid: fi.uid });
        log(st, `${c.name}: ${w.name} wins the arm wrestle (highest roll) and revives ${cat[s].name}!`);
      } else log(st, `${c.name}: ${w.name} wins the arm wrestle but has no fighter to revive.`);
      break;
    }
    case 'sun_jerry': {
      const w = rollOff(st, p, c.name);
      const d = drawFighterToParty(st, w, cat);
      log(st, `${c.name}: ${w.name} says "Hairy Jerry!" first (highest roll)${d ? ` and ${cat[d].name} joins the party` : ' but has no fighter to draw'}.`);
      break;
    }
  }
  return { ok: true };
}

// ---------------------------------------------------------------- lifecycle

export interface CreateOptions { mode?: GameMode; decks: DeckInput[]; catalog: Card[] | Catalog; seed: number }

export function create(opts: CreateOptions): GameState {
  const mode = opts.mode ?? (opts.decks.length === 4 ? '2v2' : '1v1');
  const rules = MODE_RULES[mode];
  if (opts.decks.length !== rules.players) throw new Error(`${mode} needs ${rules.players} players`);
  const cat: Catalog = Array.isArray(opts.catalog) ? Object.fromEntries(opts.catalog.map((c) => [c.slug, c])) : opts.catalog;
  const st: GameState = {
    mode, seq: 0, turn: 0, first: 0, turnNo: 1, phase: 'act', wild: null, pendingWild: null,
    players: [], teams: [{ level: 0, kills: 0 }, { level: 0, kills: 0 }], log: [], events: [], winner: null, winReason: null,
    seed: opts.seed >>> 0, rng: opts.seed >>> 0, nextUid: 1, nextLog: 1,
  };
  const perTeam = rules.players / 2;
  opts.decks.forEach((d, i) => {
    const gl = cat[d.gooflord]?.type === 'gooflord' ? d.gooflord : Object.values(cat).find((c) => c.type === 'gooflord')?.slug ?? d.gooflord;
    const p: PlayerState = {
      id: d.player.id, name: d.player.name, avatar: d.player.avatar, ai: !!d.ai, team: Math.floor(i / perTeam), seat: i % perTeam, gooflord: gl,
      party: [], fighterDeck: shuffleWith(capCorrupted(d.fighters, cat).filter((s) => cat[s] && (cat[s].type === 'fighter' || cat[s].type === 'corrupted')), st),
      itemDeck: shuffleWith(d.items.filter((s) => cat[s]?.type === 'item'), st), hand: [], discard: [], kills: 0, tames: 0,
      usedAbility: false, usedGameAbility: false, itemUsed: false, itemBlocked: false, buffs: {}, targetDebuff: {}, debuff: 0, rollBonus: 0, skipNext: false,
    };
    // Corrupted cards never start in play — they sink to the bottom of the deck.
    const corr = p.fighterDeck.filter((s) => cat[s].type === 'corrupted');
    p.fighterDeck = p.fighterDeck.filter((s) => cat[s].type !== 'corrupted').concat(corr);
    st.players.push(p);
  });
  for (const p of st.players) {
    for (let i = 0; i < 3; i++) dealOpening(st, p, cat);
    for (let i = 0; i < 3; i++) drawItem(st, p);
  }
  st.first = nextRandom(st) < 0.5 ? 0 : perTeam; // a random team goes first
  st.turn = st.first;
  st.events = [];
  log(st, `${st.players[st.turn].name} goes first.`);
  startTurn(st, cat);
  return st;
}

/** Opening party: the first fighter from the top at or below OPENING_MAX_LEVEL, so a lucky shuffle can't start you miles ahead. */
function dealOpening(st: GameState, p: PlayerState, cat: Catalog) {
  if (p.party.length >= PARTY_MAX) return;
  let i = p.fighterDeck.findIndex((s) => cat[s]?.type === 'fighter' && cat[s].level <= OPENING_MAX_LEVEL);
  if (i < 0) i = joinIdx(p, cat, false);
  if (i < 0) return;
  addToParty(st, p, p.fighterDeck.splice(i, 1)[0], cat, false);
}

function startTurn(st: GameState, cat: Catalog) {
  const p = st.players[st.turn];
  p.usedAbility = false; p.itemUsed = false; p.sacrificed = false; p.buffs = {}; p.targetDebuff = {}; p.debuff = 0; p.rollBonus = 0;
  for (const f of p.party) { f.exhausted = !!f.stunned; f.stunned = false; }
  for (const f of p.party.slice()) {
    if (f.burning && !f.shielded && baseLevel(cat, f) > 0) {
      f.bonus -= 1;
      emit(st, { kind: 'buff', player: st.turn, uid: f.uid, delta: -1 });
      log(st, `${cat[f.slug].name} burns: −1 level.`);
    }
    f.shielded = false;
  }
  st.wild = null; st.pendingWild = null; st.phase = 'act'; st.choice = null;
  emit(st, { kind: 'turn', player: st.turn, turnNo: st.turnNo });
  if (p.party.length === 0) {
    drawFighterToParty(st, p, cat);
    drawFighterToParty(st, p, cat);
    log(st, `Turn ${st.turnNo}: ${p.name} had no fighters and draws 2 starters.`);
    return;
  }
  st.wild = drawFighter(st, p);
  if (st.wild) {
    emit(st, { kind: 'wild', player: st.turn, slug: st.wild });
    log(st, `Turn ${st.turnNo}: ${p.name} meets a wild ${cat[st.wild].name} (L${cat[st.wild].level}).`);
  } else log(st, `Turn ${st.turnNo}: ${p.name}'s deck is empty. No wild fighter.`);
}

function advanceTurn(st: GameState, cat: Catalog) {
  const p = st.players[st.turn];
  p.itemBlocked = false;
  let guard = 0;
  do {
    st.turn = (st.turn + 1) % st.players.length;
    if (st.turn === st.first) st.turnNo++;
    checkWin(st);
    if (st.winner !== null) return;
    const n = st.players[st.turn];
    if (n.skipNext || (n.skipTurns ?? 0) > 0) {
      if (n.skipNext) n.skipNext = false; else n.skipTurns = (n.skipTurns ?? 0) - 1;
      emit(st, { kind: 'skip', player: st.turn });
      log(st, `${n.name}'s turn is skipped!`);
      continue;
    }
    break;
  } while (guard++ < st.players.length + 1);
  startTurn(st, cat);
}

function checkWin(st: GameState) {
  if (st.winner !== null) return;
  const wl = winLevel(st);
  for (let t = 0; t < st.teams.length; t++) {
    if (st.teams[t].level >= wl) {
      st.winner = t; st.winReason = 'level';
      emit(st, { kind: 'win', team: t, reason: 'level' });
      log(st, `${teamName(st, t)} reached Gooflord level ${wl} and wins!`);
      return;
    }
  }
  if (st.turnNo > turnLimit(st)) decideOnPoints(st, 'turn_limit');
}
/** No winner by level: the higher level wins, then more kills, then the team that went second. */
function decideOnPoints(st: GameState, reason: 'turn_limit' | 'time_limit') {
  const [a, b] = st.teams;
  const secondTeam = st.players[st.first].team === 0 ? 1 : 0;
  st.winner = a.level !== b.level ? (a.level > b.level ? 0 : 1) : a.kills !== b.kills ? (a.kills > b.kills ? 0 : 1) : secondTeam;
  st.winReason = reason;
  emit(st, { kind: 'win', team: st.winner, reason });
  log(st, `${reason === 'turn_limit' ? 'Turn limit reached' : "Time's up"} — ${teamName(st, st.winner)} wins on ${a.level !== b.level ? 'level' : a.kills !== b.kills ? 'kills' : 'going second'}.`);
}
/** The battle clock ran out: end the game now and decide it like the turn limit. */
export function timeUp(st: GameState): ActResult {
  if (st.winner !== null) return { ok: false, error: 'The game is over' };
  st.events = [];
  decideOnPoints(st, 'time_limit');
  st.seq++;
  return { ok: true };
}
/** Goose/Banana/Warrior Blob Fish, and Bob Fish ("a normal blob fish" per Ben's card) — Corrupted Daisy's nemeses. */
export const isBlobFish = (c?: Card) => !!c && /bl?ob ?fish/i.test(c.name);
export const teamName = (st: GameState, team: number) => st.players.filter((p) => p.team === team).map((p) => p.name).join(' & ');

/** Remove a fighter from play, award levels, fire death hooks. Returns the level gain. */
function kill(st: GameState, killer: PlayerState, victimOwner: PlayerState, idx: number, cat: Catalog, byFighter: Fighter | null): number {
  const f = victimOwner.party.splice(idx, 1)[0];
  const c = cat[f.slug];
  victimOwner.discard.push(c.type === 'item' ? itemEntry(f.slug) : f.slug);
  let gain = c.type === 'corrupted' ? 2 : 1;
  if (byFighter && byFighter.slug === 'corrupted-daisy' && isBlobFish(c)) gain = 2;
  killer.kills++; st.teams[killer.team].kills++;
  addLevel(st, killer.team, gain);
  emit(st, { kind: 'kill', player: pidx(st, killer), victimPlayer: pidx(st, victimOwner), uid: f.uid, slug: f.slug, gain });
  if (c.ability === 'corr_daisy' && byFighter && isBlobFish(cat[byFighter.slug])) {
    addLevel(st, victimOwner.team, -5); addLevel(st, killer.team, 5);
    log(st, `A Blob Fish killed Corrupted Daisy! ${victimOwner.name} loses 5 levels, ${killer.name} gains 5 more.`);
  }
  if (f.insured) { drawItem(st, victimOwner); drawItem(st, victimOwner); log(st, `Green Potion pays out: ${victimOwner.name} draws 2 items.`); }
  // Corrupted BeoTeo: every death anywhere makes it 3 levels stronger
  for (const x of st.players) for (const b of x.party) if (cat[b.slug]?.ability === 'corr_beoteo') {
    b.bonus += 3;
    emit(st, { kind: 'buff', player: pidx(st, x), uid: b.uid, delta: 3 });
    log(st, `${cat[b.slug].name} feeds on the death: +3 levels.`);
  }
  if (c.ability === 'summon') {
    if (c.on_death === 'draw_fighter') { const d = drawFighterToParty(st, victimOwner, cat); if (d) log(st, `${c.name} died: ${cat[d].name} joins ${victimOwner.name}'s party.`); }
    else { drawItem(st, victimOwner); log(st, `${c.name} died: ${victimOwner.name} draws an item.`); }
  }
  // King Plum: only fighter kills (not Magical Stick). Roll 12 or lower (a plain d20: roll bonuses don't help a low roll).
  if (byFighter && cat[killer.gooflord]?.ability === 'king_plum' && (killer.plumUses ?? 0) < KING_PLUM_MAX_USES && victimOwner.party.length) {
    const r = 1 + Math.floor(nextRandom(st) * 20);
    emit(st, { kind: 'roll', player: pidx(st, killer), value: r, need: 12, ok: r <= 12, reason: 'King Plum' });
    if (r <= 12) {
      killer.plumUses = (killer.plumUses ?? 0) + 1;
      const v = discardFighter(st, victimOwner, lowestIdx(cat, victimOwner.party), cat);
      if (v) log(st, `King Plum rolls ${r}: ${victimOwner.name} discards ${cat[v.slug].name} (${killer.plumUses}/${KING_PLUM_MAX_USES}).`);
    } else log(st, `King Plum rolls ${r}. Nothing happens.`);
  }
  return gain;
}

// ---------------------------------------------------------------- actions

/**
 * Apply one action for the player at `playerIndex`. Mutates `st` in place and returns whether it was legal.
 * Illegal actions leave the state untouched (apart from nothing) and are reported, so the server can reject them.
 */
export function act(st: GameState, playerIndex: number, action: Action, cat: Catalog): ActResult {
  st.events = [];
  if (st.winner !== null) return { ok: false, error: 'The game is over' };
  if (playerIndex !== st.turn) return { ok: false, error: 'Not your turn' };
  const r = apply(st, action, cat);
  if (r.ok) { checkWin(st); st.seq++; }
  return r;
}

function apply(st: GameState, action: Action, cat: Catalog): ActResult {
  const p = st.players[st.turn];
  const me = st.turn;
  const gl = cat[p.gooflord] ?? ({} as Card);
  const bad = (error: string): ActResult => ({ ok: false, error });
  if (st.phase === 'choose' && action.type !== 'choose' && action.type !== 'end') return bad(`Finish ${st.choice?.source ?? 'your choice'} first`);
  switch (action.type) {
    case 'ability':
      return useAbility(st, p, gl, action, cat);

    case 'choose':
      if (st.phase !== 'choose' || st.choice?.player !== me) return bad('Nothing to choose');
      return resolveChoice(st, action.picks, cat);

    case 'corrupted': {
      if (st.phase !== 'act') return bad('Decide what to do with the tamed fighter first');
      const f = p.party[action.fighter];
      if (!f) return bad('No such fighter');
      const ab = cat[f.slug]?.ability ?? '';
      if (!TAP_ABILITIES.includes(ab) && ab !== 'corr_daisy' && ab !== 'corr_beoteo') return bad(`${cat[f.slug]?.name ?? 'That fighter'} has no ability to use`);
      if (!corruptedReady(f, cat)) return bad(f.ready === false ? `${cat[f.slug].name}'s ability is already used` : `${cat[f.slug].name}'s ability is always on`);
      return useCorrupted(st, p, f, action, cat);
    }

    case 'item': {
      if (st.phase !== 'act') return bad('Decide what to do with the tamed fighter first');
      if (p.itemUsed) return bad('You already used an item this turn');
      if (p.itemBlocked) return bad("Remote: you can't use an item this turn");
      const s = p.hand[action.hand];
      const c = s ? cat[s] : undefined;
      if (!c) return bad('No such card in hand');
      p.hand.splice(action.hand, 1); // the card leaves the hand before it resolves (its effect may touch the hand)
      const r = applyItem(st, p, c, action.targetPlayer, action.target, cat);
      if (!r.ok) { p.hand.splice(action.hand, 0, s); return r; }
      if (c.ability !== 'summon') p.discard.push(itemEntry(s));
      p.itemUsed = true;
      emit(st, { kind: 'item', player: me, slug: s, targetPlayer: action.targetPlayer, target: action.target });
      return { ok: true };
    }

    case 'tame': {
      if (!st.wild) return bad('There is no wild fighter to tame');
      const idxs = [...new Set(action.fighters)];
      if (!idxs.length) return bad('Choose at least one fighter');
      const sel = idxs.map((i) => p.party[i]);
      if (sel.some((f) => !f)) return bad('No such fighter');
      if (sel.some((f) => f.exhausted)) return bad('An exhausted fighter cannot tame');
      const sum = sel.reduce((a, f) => a + power(p, f, cat), 0);
      const need = cat[st.wild].level - p.debuff;
      for (const f of sel) f.exhausted = true;
      const ok = sum >= need;
      emit(st, { kind: 'tame', player: me, slug: st.wild, sum, need, ok });
      if (ok) {
        p.tames++;
        st.pendingWild = st.wild; st.wild = null; st.phase = 'keep';
        log(st, `${p.name} tamed ${cat[st.pendingWild].name} with ${sum} vs ${need}.`);
      } else {
        log(st, `${p.name}'s ${sum} wasn't enough for ${cat[st.wild].name} (${need}). It escapes.`);
        p.discard.push(st.wild); st.wild = null;
      }
      return { ok: true };
    }

    case 'keep': {
      const w = st.pendingWild;
      if (!w || st.phase !== 'keep') return bad('Nothing to keep');
      st.pendingWild = null; st.phase = 'act';
      if (action.keep && p.party.length < PARTY_MAX) {
        addToParty(st, p, w, cat, true);
        emit(st, { kind: 'keep', player: me, slug: w, kept: true });
        log(st, `${cat[w].name} joins ${p.name}'s party.`);
      } else {
        p.discard.push(w);
        emit(st, { kind: 'keep', player: me, slug: w, kept: false });
        drawItem(st, p);
        log(st, `${p.name} sacrificed ${cat[w].name} for an item.`);
      }
      return { ok: true };
    }

    case 'sacrifice': { // give up a party fighter for an item: it costs that fighter's action, once per turn
      if (st.phase !== 'act') return bad('Decide what to do with the tamed fighter first');
      if (p.sacrificed) return bad('You can only sacrifice one fighter a turn');
      const f = p.party[action.fighter];
      if (!f) return bad('No such fighter');
      if (f.exhausted) return bad(`${cat[f.slug].name} is exhausted`);
      emit(st, { kind: 'sacrifice', player: me, uid: f.uid, slug: f.slug });
      discardFighter(st, p, action.fighter, cat);
      p.sacrificed = true;
      const hand = p.hand.length;
      const drew = drawItem(st, p);
      log(st, `${p.name} sacrificed ${cat[f.slug].name}${!drew ? ' (no item to draw)' : p.hand.length > hand ? ' for an item' : ' for an item (hand full, it goes to the discard)'}.`);
      return { ok: true };
    }

    case 'skipWild': {
      if (!st.wild) return bad('There is no wild fighter');
      p.discard.push(st.wild);
      log(st, `${p.name} let ${cat[st.wild].name} wander off.`);
      st.wild = null;
      return { ok: true };
    }

    case 'raid': {
      if (st.phase !== 'act') return bad('Decide what to do with the tamed fighter first');
      const a = p.party[action.attacker];
      if (!a) return bad('No such fighter');
      if (a.exhausted) return bad(`${cat[a.slug].name} is exhausted`);
      const o = opponent(st, p, action.targetPlayer);
      if (action.targetPlayer != null && st.players[action.targetPlayer]?.team === p.team) return bad("You can't raid your own team");
      if (teamDefenceless(st, o.team)) {
        a.exhausted = true;
        const steal = Math.min(1, st.teams[o.team].level);
        addLevel(st, o.team, -1); addLevel(st, p.team, 1);
        emit(st, { kind: 'raid_gooflord', player: me, attacker: action.attacker, targetPlayer: pidx(st, o) });
        log(st, `${cat[a.slug].name} raids ${o.name}'s defenceless Gooflord! ${p.name} +1 level${steal ? `, ${o.name} -1` : ''}.`);
        return { ok: true };
      }
      if (action.target == null) return bad('Pick an enemy fighter to raid');
      const t = o.party[action.target];
      if (!t) return bad('No such enemy fighter');
      a.exhausted = true;
      const ap = power(p, a, cat), dp = defence(p, t, cat);
      const oi = pidx(st, o);
      if (ap > dp) {
        emit(st, { kind: 'raid', player: me, attacker: action.attacker, targetPlayer: oi, target: action.target, ap, dp, result: 'kill' });
        const g = kill(st, p, o, action.target, cat, a);
        log(st, `${cat[a.slug].name} (${ap}) kills ${cat[t.slug].name} (${dp}). ${p.name} +${g} level.`);
      } else if (ap < dp) {
        emit(st, { kind: 'raid', player: me, attacker: action.attacker, targetPlayer: oi, target: action.target, ap, dp, result: 'die' });
        const g = kill(st, o, p, action.attacker, cat, t);
        log(st, `${cat[a.slug].name} (${ap}) falls to ${cat[t.slug].name} (${dp}). ${o.name} +${g} level.`);
      } else {
        emit(st, { kind: 'raid', player: me, attacker: action.attacker, targetPlayer: oi, target: action.target, ap, dp, result: 'tie' });
        log(st, `${cat[a.slug].name} and ${cat[t.slug].name} tie at ${ap}. Both stand down.`);
      }
      return { ok: true };
    }

    case 'end': {
      if (st.phase === 'choose') resolveChoice(st, null, cat);
      if (st.phase === 'keep') apply(st, { type: 'keep', keep: true }, cat);
      if (st.wild) apply(st, { type: 'skipWild' }, cat);
      checkWin(st);
      if (st.winner !== null) return { ok: true };
      if (gl.ability === 'bog') {
        const roll = d20(st, p);
        emit(st, { kind: 'roll', player: me, value: roll, need: 17, ok: roll >= 17, reason: 'Bog' });
        if (roll >= 17) {
          emit(st, { kind: 'extra_turn', player: me });
          log(st, `Bog rolls ${roll} — extra turn!`);
          p.itemBlocked = false;
          startTurn(st, cat);
          return { ok: true };
        }
        log(st, `Bog rolls ${roll}. No extra turn.`);
      }
      advanceTurn(st, cat);
      return { ok: true };
    }
  }
  return bad('Unknown action');
}

function useAbility(st: GameState, p: PlayerState, gl: Card, action: Extract<Action, { type: 'ability' }>, cat: Catalog): ActResult {
  const me = st.turn;
  const bad = (error: string): ActResult => ({ ok: false, error });
  if (st.phase !== 'act') return bad('Decide what to do with the tamed fighter first');
  const ab = gl.ability ?? '';
  const perTurn = (PER_TURN_ABILITIES as readonly string[]).includes(ab);
  const perGame = (PER_GAME_ABILITIES as readonly string[]).includes(ab);
  if (!perTurn && !perGame) return bad(`${gl.name ?? 'Your Gooflord'} has no activated ability`);
  if (perTurn && p.usedAbility) return bad('Ability already used this turn');
  if (perGame && p.usedGameAbility) return bad('Ability already used this game');
  const f = action.target != null ? p.party[action.target] : undefined;
  const o = opponent(st, p, action.targetPlayer);
  switch (ab) {
    case 'blob_bear': {
      if (!f) return bad('Choose one of your fighters');
      let n = 0;
      while (nextRandom(st) < 0.5 && n < 10) n++;
      p.buffs[f.uid] = (p.buffs[f.uid] || 0) + 2 * n;
      emit(st, { kind: 'coins', player: me, heads: n });
      if (n) emit(st, { kind: 'buff', player: me, uid: f.uid, delta: 2 * n });
      log(st, `Blob Bear flips ${n} heads: ${cat[f.slug].name} gets +${2 * n}.`);
      break;
    }
    case 'tooth_catcher': {
      if (!f) return bad('Choose one of your fighters');
      const r = d20(st, p);
      emit(st, { kind: 'roll', player: me, value: r, need: 11, ok: r >= 11, reason: 'Tooth Catcher' });
      if (r >= 11) { p.buffs[f.uid] = (p.buffs[f.uid] || 0) + 3; emit(st, { kind: 'buff', player: me, uid: f.uid, delta: 3 }); log(st, `Tooth Catcher wins rock-paper-scissors (${r}): ${cat[f.slug].name} +3.`); }
      else log(st, `Tooth Catcher loses rock-paper-scissors (${r}).`);
      break;
    }
    case 'ducky': {
      const r = d20(st, p);
      const i = p.discard.findIndex(isItemEntry);
      emit(st, { kind: 'roll', player: me, value: r, need: 11, ok: r >= 11, reason: 'Ducky Wucky' });
      if (r >= 11 && i >= 0 && p.hand.length < handMax(p)) {
        const s = stripItem(p.discard.splice(i, 1)[0]); p.hand.push(s);
        emit(st, { kind: 'draw_item', player: me, slug: s });
        log(st, `Ducky Wucky wins the arm wrestle (${r}) and gets ${cat[s].name} back.`);
      } else log(st, `Ducky Wucky ${r >= 11 ? 'wins but has nothing to recover' : `loses the arm wrestle (${r})`}.`);
      break;
    }
    case 'key_master': {
      const r = d20(st, p);
      emit(st, { kind: 'roll', player: me, value: r, need: 10, ok: r >= 10, reason: 'Key Master' });
      const n = Math.min(3, p.itemDeck.length);
      if (r >= 10 && n) {
        log(st, `Key Master rolls ${r}: ${p.name} looks at the top ${n} item${n === 1 ? '' : 's'} and can reorder them.`);
        ask(st, { player: me, kind: 'order', from: me, pile: 'item', options: p.itemDeck.slice(0, n), count: n, source: 'Key Master' });
      } else log(st, `Key Master rolls ${r}${r >= 10 ? ' but the item deck is empty' : ''}. Nothing happens.`);
      break;
    }
    case 'chunky': {
      const ps = [...new Set(action.picks ?? [])];
      if (ps.length !== 2 || ps.some((i) => !p.party[i])) return bad('Choose 2 of your fighters to sacrifice');
      if (ps.some((i) => baseLevel(cat, p.party[i]) <= 5)) return bad('Both fighters must be above level 5');
      const names = ps.map((i) => cat[p.party[i].slug].name);
      for (const i of ps.sort((a, b) => b - a)) discardFighter(st, p, i, cat);
      addLevel(st, p.team, 1);
      log(st, `Chunky Blob Fish eats ${names.join(' and ')}: ${p.name} +1 Gooflord level.`);
      break;
    }
    case 'bubble': {
      if (!f) return bad('Choose one of your fighters');
      const heads = nextRandom(st) < 0.5;
      emit(st, { kind: 'coins', player: me, heads: heads ? 1 : 0 });
      if (heads) { addLevel(st, p.team, -2); log(st, `Bubble flips heads: ${p.name} loses 2 Gooflord levels.`); }
      else { f.shielded = true; log(st, `Bubble flips tails: ${cat[f.slug].name}'s level can't be lowered until ${p.name}'s next turn.`); }
      break;
    }
    case 'acidity': {
      const heads = nextRandom(st) < 0.5;
      emit(st, { kind: 'coins', player: me, heads: heads ? 1 : 0 });
      if (heads) { addLevel(st, o.team, -1); log(st, `Acidity flips heads: ${o.name} loses a Gooflord level.`); }
      else { addLevel(st, p.team, -1); log(st, `Acidity flips tails: ${p.name} loses a Gooflord level.`); }
      break;
    }
    case 'grumpy': {
      let n = 0;
      while (n < 11 && nextRandom(st) < 0.5) n++;
      emit(st, { kind: 'coins', player: me, heads: n });
      if (n >= 11) {
        st.winner = p.team; st.winReason = 'ability';
        emit(st, { kind: 'win', team: p.team, reason: 'ability' });
        log(st, `GRumpyy pants flips 11 heads in a row! ${teamName(st, p.team)} wins!`);
      } else log(st, `GRumpyy pants flips ${n} head${n === 1 ? '' : 's'} in a row. Needs 11.`);
      break;
    }
    case 'party_dice': {
      const r = d20(st, p);
      emit(st, { kind: 'roll', player: me, value: r, need: 20, ok: r >= 20, reason: 'Party Dice' });
      const wipe = (x: PlayerState) => { const keep = highestIdx(cat, x.party); for (let k = x.party.length - 1; k >= 0; k--) if (k !== keep) discardFighter(st, x, k, cat); };
      if (r >= 20 && o.party.length) { wipe(o); log(st, `Party Dice rolls a 20! ${o.name} keeps only one fighter.`); }
      else if (r < 10 && p.party.length) { wipe(p); log(st, `Party Dice rolls ${r}… ${p.name} keeps only one fighter. Ouch.`); }
      else log(st, `Party Dice rolls ${r}. Nothing happens.`);
      break;
    }
    case 'jimbo': {
      if (p.hand.length < HAND_MAX) return bad(`Little Jimbo needs ${HAND_MAX} or more items in hand`);
      const i = p.fighterDeck.findIndex((s) => cat[s]?.type === 'corrupted' && canJoinParty(cat, s));
      if (i < 0) return bad('No Corrupted card left in your deck');
      if (p.party.length >= PARTY_MAX) return bad('Your party is full');
      while (p.hand.length) discardFromHand(st, p, p.hand.length - 1);
      const s = p.fighterDeck.splice(i, 1)[0];
      const fi = addToParty(st, p, s, cat, false);
      emit(st, { kind: 'draw_fighter', player: me, slug: s, uid: fi.uid });
      log(st, `Little Jimbo and Thorn discard the whole hand and pull ${cat[s].name} into the party!`);
      p.usedGameAbility = true;
      emit(st, { kind: 'ability', player: me, ability: ab });
      return { ok: true };
    }
    case 'pip': {
      const gone = o.fighterDeck.splice(0, 3);
      for (const s of gone) { o.discard.push(s); emit(st, { kind: 'discard', player: pidx(st, o), slug: s, item: false }); }
      log(st, `Pip erases the top ${gone.length} fighters of ${o.name}'s deck.`);
      p.usedGameAbility = true;
      emit(st, { kind: 'ability', player: me, ability: ab });
      return { ok: true };
    }
    case 'cluck': {
      if (!canDrawToParty(p, cat)) return bad(p.party.length >= PARTY_MAX ? 'Your party is full' : 'No fighter to draw');
      if (o.party.length) { const v = discardFighter(st, o, lowestIdx(cat, o.party), cat); if (v) log(st, `Cluck makes ${o.name} laugh: ${cat[v.slug].name} is discarded.`); }
      const d = drawFighterToParty(st, p, cat);
      if (d) log(st, `${cat[d].name} joins ${p.name}'s party.`);
      p.usedGameAbility = true;
      emit(st, { kind: 'ability', player: me, ability: ab });
      return { ok: true };
    }
  }
  p.usedAbility = true;
  emit(st, { kind: 'ability', player: me, ability: ab });
  return { ok: true };
}

function applyItem(st: GameState, p: PlayerState, c: Card, targetPlayer: number | undefined, target: number | undefined, cat: Catalog): ActResult {
  const me = st.turn;
  const bad = (error: string): ActResult => ({ ok: false, error });
  const o = opponent(st, p, targetPlayer);
  if (targetPlayer != null && st.players[targetPlayer]?.team === p.team && c.target === 'enemy') return bad("That's your own team");
  const f = c.target === 'ally' && target != null ? p.party[target] : undefined;
  const e = c.target === 'enemy' && target != null ? o.party[target] : undefined;
  if (c.target === 'ally' && !f) return bad('Choose one of your fighters');
  if (c.target === 'enemy' && !e) return bad('Choose an enemy fighter');
  // "+N" from an item stays on the fighter until it dies (issue #17), and everyone can see it; a minus only lasts this turn
  const buff = (x: Fighter, n: number) => {
    if (n > 0) x.bonus += n; else p.buffs[x.uid] = (p.buffs[x.uid] || 0) + n;
    if (n) emit(st, { kind: 'buff', player: me, uid: x.uid, delta: n });
  };
  switch (c.ability) {
    case 'buff': buff(f!, c.value ?? 0); log(st, `${p.name} uses ${c.name}: ${cat[f!.slug].name} +${c.value}.`); return { ok: true };
    case 'buff_draw': buff(f!, c.value ?? 0); drawItem(st, p); log(st, `${c.name}: ${cat[f!.slug].name} +${c.value}, ${p.name} draws an item.`); return { ok: true };
    case 'd20': {
      const r = d20(st, p); const ok = r >= (c.threshold ?? 0); const d = ok ? c.value ?? 0 : c.fail ?? 0;
      emit(st, { kind: 'roll', player: me, value: r, need: c.threshold, ok, reason: c.name });
      buff(f!, d); log(st, `${c.name}: ${p.name} rolls ${r}. ${cat[f!.slug].name} gets ${d >= 0 ? '+' : ''}${d}.`); return { ok: true };
    }
    case 'insure': f!.insured = true; log(st, `${p.name} puts a Green Potion on ${cat[f!.slug].name}.`); return { ok: true };
    case 'stun': e!.stunned = true; e!.exhausted = true; log(st, `${c.name}: ${cat[e!.slug].name} can't be played on ${o.name}'s next turn.`); return { ok: true };
    case 'debuff_target': p.targetDebuff[e!.uid] = (p.targetDebuff[e!.uid] || 0) + (c.value ?? 0); log(st, `${c.name}: ${cat[e!.slug].name} loses ${c.value} levels this turn.`); return { ok: true };
    case 'kill': { const g = kill(st, p, o, target!, cat, null); log(st, `${c.name} kills ${cat[e!.slug].name}! ${p.name} +${g} level.`); return { ok: true }; }
    case 'draw_items': { const n = c.value ?? 1; for (let i = 0; i < n; i++) drawItem(st, p); log(st, `${c.name}: ${p.name} draws ${n} item${n > 1 ? 's' : ''}.`); return { ok: true }; }
    case 'shuffle_draw_fighter': {
      if (p.party.length >= PARTY_MAX) return bad('Your party is full');
      p.fighterDeck = shuffleWith(p.fighterDeck, st);
      const s = drawFighterToParty(st, p, cat);
      if (!s) return bad('No fighter to draw');
      log(st, `${c.name}: ${cat[s].name} joins ${p.name}'s party.`); return { ok: true };
    }
    case 'draw_bottom': {
      const s = drawFighterToParty(st, p, cat, true);
      if (s) { log(st, `${c.name}: ${cat[s].name} (from the bottom of the deck) joins the party.`); return { ok: true }; }
      if (!drawItem(st, p, true)) return bad('Nothing to draw');
      log(st, `${c.name}: ${p.name} draws an item from the bottom of the item deck.`); return { ok: true };
    }
    case 'everyone_draw_fighter': {
      for (const x of st.players) { const s = drawFighterToParty(st, x, cat); if (s) log(st, `Kind Gem: ${cat[s].name} joins ${x.name}'s party.`); }
      return { ok: true };
    }
    case 'block_items': o.itemBlocked = true; log(st, `${c.name}: ${o.name} can't use an item next turn.`); return { ok: true };
    case 'scry': {
      const n = Math.min(c.value ?? 3, p.fighterDeck.length);
      const top = p.fighterDeck.splice(0, n).sort((a, b) => cat[b].level - cat[a].level);
      p.fighterDeck = top.concat(p.fighterDeck);
      log(st, `${c.name}: ${p.name} peeks at the top ${n} fighter${n === 1 ? '' : 's'} and puts the strongest on top.`); return { ok: true };
    }
    case 'discard_enemy_fighter': {
      if (!o.party.length) return bad(`${o.name} has no fighters`);
      const v = discardFighter(st, o, lowestIdx(cat, o.party), cat)!;
      log(st, `${c.name}: ${o.name} discards ${cat[v.slug].name}.`); return { ok: true };
    }
    case 'd20_discard_fighter': {
      const r = d20(st, p); const ok = r >= (c.threshold ?? 12);
      emit(st, { kind: 'roll', player: me, value: r, need: c.threshold, ok, reason: c.name });
      if (ok && o.party.length) { const v = discardFighter(st, o, lowestIdx(cat, o.party), cat)!; log(st, `${c.name}: rolls ${r} — ${o.name} discards ${cat[v.slug].name}.`); }
      else log(st, `${c.name}: rolls ${r}. Nothing happens.`);
      return { ok: true };
    }
    case 'skip_turn': {
      const r = d20(st, p); const ok = r >= (c.threshold ?? 15);
      emit(st, { kind: 'roll', player: me, value: r, need: c.threshold, ok, reason: c.name });
      if (ok) { o.skipNext = true; log(st, `${c.name}: rolls ${r} — ${o.name}'s next turn is skipped!`); } else log(st, `${c.name}: rolls ${r}. Nothing happens.`);
      return { ok: true };
    }
    case 'recover_item': {
      const i = p.discard.findIndex(isItemEntry);
      if (i < 0) return bad('No item in your discard pile');
      if (p.hand.length >= handMax(p)) return bad('Your hand is full');
      const s = stripItem(p.discard.splice(i, 1)[0]); p.hand.push(s);
      emit(st, { kind: 'draw_item', player: me, slug: s });
      log(st, `${c.name} chomps ${cat[s].name} back into ${p.name}'s hand.`); return { ok: true };
    }
    case 'egg': {
      if (o.hand.length) discardFromHand(st, o, Math.floor(nextRandom(st) * o.hand.length));
      drawItem(st, p); drawItem(st, p);
      log(st, `${c.name}: ${o.name} discards an item, ${p.name} draws 2.`); return { ok: true };
    }
    case 'discard_enemy_items': {
      let n = 0; while (o.hand.length && n < (c.value ?? 3)) { discardFromHand(st, o, o.hand.length - 1); n++; }
      log(st, `${c.name}: ${o.name} discards ${n} item${n === 1 ? '' : 's'}.`); return { ok: true };
    }
    case 'summon': {
      if (p.party.length >= PARTY_MAX) return bad('Your party is full');
      const fi = addToParty(st, p, c.slug, cat, false);
      emit(st, { kind: 'draw_fighter', player: me, slug: c.slug, uid: fi.uid });
      log(st, `${c.name} becomes a level ${c.level} fighter in ${p.name}'s party.`); return { ok: true };
    }
    case 'steal_fighter':
    case 'steal_corrupted': {
      const want = c.ability === 'steal_fighter' ? 'fighter' : 'corrupted';
      if (cat[e!.slug]?.type !== want) return bad(`Choose an enemy ${want === 'fighter' ? 'fighter (not a Corrupted card)' : 'Corrupted card'}`);
      if (p.party.length >= PARTY_MAX) return bad('Your party is full');
      o.party.splice(target!, 1);
      const fi = addToParty(st, p, e!.slug, cat, false);
      emit(st, { kind: 'draw_fighter', player: me, slug: e!.slug, uid: fi.uid });
      log(st, `${c.name}: ${p.name} steals ${cat[e!.slug].name} from ${o.name}'s party!`); return { ok: true };
    }
    case 'dumbo': {
      const before = f!.bonus;
      f!.bonus = 1 - (cat[f!.slug]?.level ?? 0);
      emit(st, { kind: 'buff', player: me, uid: f!.uid, delta: f!.bonus - before });
      addLevel(st, p.team, 1);
      log(st, `${c.name}: ${cat[f!.slug].name} is level 1 for good, and ${p.name} gets +1 Gooflord level.`); return { ok: true };
    }
    case 'peek_items':
    case 'peek_fighters': {
      const items = c.ability === 'peek_items';
      if (items) p.itemDeck = shuffleWith(p.itemDeck, st); else p.fighterDeck = shuffleWith(p.fighterDeck, st);
      const top = (items ? p.itemDeck : p.fighterDeck).slice(0, 2);
      log(st, `${c.name}: ${p.name} shuffles their ${items ? 'item' : 'fighter'} deck and looks at the top ${top.length}.`);
      if (top.length) ask(st, { player: me, kind: 'peek', from: me, pile: items ? 'item' : 'fighter', options: top, count: 0, source: c.name });
      return { ok: true };
    }
    case 'five_eyes': {
      if (o.hand.length < 5) return bad(`${o.name} doesn't have 5 items in hand`);
      log(st, `${c.name}: ${p.name} looks at ${o.name}'s hand…`);
      ask(st, { player: me, kind: 'steal', from: pidx(st, o), pile: 'hand', options: o.hand.slice(), count: 1, source: c.name });
      return { ok: true };
    }
    case 'anihilate': {
      if (!p.party.length) return bad('You need a fighter of your own to sacrifice');
      const g = kill(st, p, o, target!, cat, null);
      const v = p.party.length ? discardFighter(st, p, lowestIdx(cat, p.party), cat) : null;
      log(st, `${c.name} kills ${cat[e!.slug].name} (${p.name} +${g} level)${v ? ` and ${p.name} sacrifices ${cat[v.slug].name}` : ''}.`); return { ok: true };
    }
    case 'deadly_wish': {
      const got = [drawFighterToParty(st, o, cat), drawFighterToParty(st, o, cat)].filter((s): s is string => !!s);
      const big = got.some((s) => cat[s].level > (c.threshold ?? 10));
      if (big) addLevel(st, o.team, -(c.value ?? 3));
      log(st, `${c.name}: ${o.name} draws ${got.length ? got.map((s) => cat[s].name).join(' and ') : 'nothing'}${big ? ` — one is above level ${c.threshold ?? 10}, so ${o.name} loses ${c.value ?? 3} Gooflord levels!` : '.'}`); return { ok: true };
    }
    case 'draw_stun': {
      const n = c.value ?? 1; for (let i = 0; i < n; i++) drawItem(st, p);
      e!.stunned = true; e!.exhausted = true;
      log(st, `${c.name}: ${p.name} draws ${n} item${n > 1 ? 's' : ''} and ${cat[e!.slug].name} can't be played on ${o.name}'s next turn.`); return { ok: true };
    }
    case 'roll_bonus': p.rollBonus += c.value ?? 3; log(st, `${c.name}: ${p.name}'s rolls get +${c.value} this turn.`); return { ok: true };
    case 'd20_revive_corrupted': {
      const r = d20(st, p); const i = p.discard.findIndex((s) => !isItemEntry(s) && cat[s]?.type === 'corrupted'); const ok = r >= (c.threshold ?? 17);
      emit(st, { kind: 'roll', player: me, value: r, need: c.threshold, ok: ok && i >= 0, reason: c.name });
      if (ok && i >= 0) { const s = p.discard.splice(i, 1)[0]; p.fighterDeck = shuffleWith(p.fighterDeck.concat([s]), st); log(st, `${c.name}: rolls ${r} — ${cat[s].name} is shuffled back into the deck.`); }
      else log(st, `${c.name}: rolls ${r}${i < 0 ? ' but no Corrupted card in the discard' : ''}. Nothing happens.`);
      return { ok: true };
    }
  }
  return bad(`${c.name} can't be played`);
}

/** A player gives up (or is timed out after disconnecting). Their team loses immediately. */
export function forfeit(st: GameState, playerIndex: number): ActResult {
  if (st.winner !== null) return { ok: false, error: 'The game is over' };
  const p = st.players[playerIndex];
  if (!p) return { ok: false, error: 'No such player' };
  st.events = [];
  st.winner = p.team === 0 ? 1 : 0; st.winReason = 'forfeit';
  emit(st, { kind: 'forfeit', player: playerIndex });
  emit(st, { kind: 'win', team: st.winner, reason: 'forfeit' });
  log(st, `${p.name} forfeits. ${teamName(st, st.winner)} wins.`);
  st.seq++;
  return { ok: true };
}

// ---------------------------------------------------------------- views

/** Can `seat` (-1 = spectator) see player `i`'s hand? Your own, and an AI teammate's. */
export const handVisible = (st: GameState, seat: number, i: number) => {
  const me = st.players[seat], p = st.players[i];
  return i === seat || (!!me && p?.team === me.team && !!p?.ai) || (p?.revealUntil ?? 0) >= st.turnNo; // Corrupted King Eye shows a hand to everyone
};
/** Redact events for one seat: cards drawn into a hand it can't see lose their slug. */
export function eventsFor(st: GameState, seat: number, events: GameEvent[]): GameEvent[] {
  return events.map((e) => (e.kind === 'draw_item' && !handVisible(st, seat, e.player) ? { kind: e.kind, player: e.player } : e));
}
/** Redact the state for one seat (or a spectator with seat -1): opponents' hands and all deck orders are hidden. */
export function view(st: GameState, seat: number, extra: { turnEndsAt?: number | null; matchEndsAt?: number | null; connected?: boolean[] } = {}): GameView {
  const players: PlayerView[] = st.players.map((p, i) => {
    const { fighterDeck, itemDeck, hand, ...rest } = p;
    const visibleHand = handVisible(st, seat, i);
    return { ...rest, fighterDeckCount: fighterDeck.length, itemDeckCount: itemDeck.length, hand: visibleHand ? hand.slice() : null, handCount: hand.length, connected: extra.connected?.[i] };
  });
  const { players: _p, rng: _r, seed: _s, events, choice, ...rest } = st;
  return { ...rest, choice: choice && choice.player !== seat ? { ...choice, options: [] } : choice ?? null, events: eventsFor(st, seat, events), players, me: seat, turnEndsAt: extra.turnEndsAt ?? null, matchEndsAt: extra.matchEndsAt ?? null };
}

export function cardsInPlay(st: GameState, p: PlayerState): string[] {
  const out = [...p.fighterDeck, ...p.itemDeck, ...p.hand, ...p.discard.map((s) => (isItemEntry(s) ? stripItem(s) : s)), ...p.party.map((f) => f.slug)];
  if (st.players[st.turn] === p) { if (st.wild) out.push(st.wild); if (st.pendingWild) out.push(st.pendingWild); }
  return out;
}
